import {
  useLazyGetGuestJourneySettingsQuery,
  useLazyListAdminStoresOverallQuery,
} from 'api/client-domain/client-domain';
import { GuestJourneyBenchmarkCuisine } from 'api/client-domain/client-domain.types';
import { GuestJourneyBenchmarkTimeframe } from 'api/reports-service/guest-journey-reports.types';
import { toStageOptions } from 'api/reports-service/guest-journey-settings.utils';
import {
  DateRangeOption,
  getDateRangeOptions,
  makeDateRanges,
} from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import {
  luxonDateSearchParam,
  stringListSearchParam,
  stringSearchParam,
} from 'components/search/search.utils';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { getMerchantId } from 'util/merchant-id';

/**
 * Param visibility config — every Guest Journey tab declares the full superset
 * of search params so that values persist across tab navigation. Each entry
 * controls only whether the corresponding filter chip is rendered on the
 * toolbar; the param itself is always registered.
 */
export type GuestJourneySearchParamsConfig = {
  storeIds: boolean;
  /**
   * Toolbar-level date range filter (Movement / Offer Impact / Details).
   */
  range: boolean;
  /**
   * In-chart-only date range scoped to the Member Count Trends chart on
   * Active / Sliders / Churn. Declared on every tab so it survives tab
   * navigation; never rendered on the toolbar.
   */
  trendsRange: boolean;
  /**
   * Single-select stage filter (Stage Movement only).
   */
  stage: boolean;
  /**
   * Single-select offer filter (Offer Impact only).
   */
  offer: boolean;
  /**
   * Offer filter options for Offer Impact, fetched up front by the bootstrap.
   * Only the Offer Impact tab supplies these; other tabs leave the param hidden
   * with empty options.
   */
  offerOptions?: { id: string; label: string }[];
  /** Default offer for Offer Impact — the first GJ offer with impact data. */
  offerDefault?: string;
  /**
   * Customer-type single-select (Details only).
   */
  customerType: boolean;
  /**
   * Sign-up source single-select (Retention only).
   */
  source?: boolean;
  /**
   * Comparison-period single-select (Retention only).
   */
  compare?: boolean;
  /**
   * Use the "Sign Up Date Range" toolbar label instead of "Date Range"
   * (Retention only).
   */
  rangeSignUpLabel?: boolean;
  /**
   * Override the toolbar date-range presets. Benchmarks restricts these to
   * "Year to date" + prior full year; defaults to the rolling-window presets.
   */
  rangeOptions?: DateRangeOption[];
  /**
   * Benchmark data-release year single-select (Benchmarks only).
   */
  benchmarkYear?: boolean;
  /**
   * Benchmark cuisine-category single-select (Benchmarks only).
   */
  benchmarkCuisine?: boolean;
  /**
   * Year filter options for Benchmarks, fetched up front by the bootstrap.
   * Only the Benchmarks tab supplies these; other tabs leave the param hidden
   * with empty options.
   */
  benchmarkYearOptions?: GuestJourneyBenchmarkTimeframe[];
  /** Cuisine filter options for Benchmarks ({ displayLabel, cuisine } pairs). */
  benchmarkCuisineOptions?: GuestJourneyBenchmarkCuisine[];
  /** Default cuisine for Benchmarks — the brand's own cuisine. */
  benchmarkCuisineDefault?: string;
};

/**
 * The BE values the Benchmarks tab must have in hand before it can render (all
 * from `GET guest-journey/cuisine` + the benchmark option endpoints):
 * `brandCuisine` is the client's own cuisine as a human-readable title
 * (`displayTitle`) for the "Brand Cuisine" KPI; `cuisineDefault` is the same
 * cuisine as its machine slug (`cuisine`), which seeds the required
 * `benchmarkCuisine` filter default and must match a `cuisineOptions` value;
 * plus the cuisine + year filter options. Fetched and blocked on by the
 * Benchmarks bootstrap so the filters get valid, synchronous defaults (no
 * `asyncOptions`).
 */
export type BenchmarksBootstrapData = {
  brandCuisine: string;
  cuisineDefault: string;
  cuisineOptions: GuestJourneyBenchmarkCuisine[];
  yearOptions: GuestJourneyBenchmarkTimeframe[];
};

/**
 * The offer list the Offer Impact tab must have in hand before it can render its
 * filter or fire its queries. The summary/sankey endpoints require a real
 * `offerIds` and 400 without one, so the tab fetches the GJ-scoped offer list up
 * front (`GET guest-journey/offers`) and seeds the *required* `offer` filter with
 * a valid default (the first offer) instead of a racy `asyncOptions` — the same
 * approach as the Benchmarks bootstrap. There is intentionally no "All Offers"
 * option (it was never a design choice and is not a valid BE selection).
 */
export type OfferImpactBootstrapData = {
  offerOptions: { id: string; label: string }[];
  offerDefault: string;
};

const TRENDS_RANGE_DEFAULT_END = () => DateTime.local().endOf('day');
const TRENDS_RANGE_DEFAULT_START = () =>
  DateTime.local().minus({ days: 90 }).startOf('day');

// The toolbar date filter is preset-only, so its default must match a preset
// exactly (the presets are anchored 2 days back via `lpAnchor`, so a hand-rolled
// "today − 365 days" would resolve to a custom range, not the Last-365-Days
// preset). Deriving from the preset guarantees the chip + drawer show
// "Last 365 Days" and that the default query matches the preset's query.
// Default is Last 365 Days across every GJ tab that shows the range filter
// (Offer Impact, Stage Movement, Details, Retention) — sc-371890.
const RANGE_DEFAULT_START = () => makeDateRanges().lpLast365Days[0];
const RANGE_DEFAULT_END = () => makeDateRanges().lpLast365Days[1];

export const useLocationParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [triggerStores] = useLazyListAdminStoresOverallQuery();

  return [
    stringListSearchParam('storeIds', {
      defaultValue: ['ALL'],
      'data-testid': 'GuestJourney.LocationsFilter',
      options: [
        {
          label: t('page.guestJourney.filters.locations.all'),
          id: 'ALL',
        },
      ],
      asyncOptions: async () => {
        const response = await triggerStores({ merchantid: getMerchantId() });
        const dataArray = response?.data?.stores.map(store => ({
          label: store.title,
          id: store.storeId,
        }));

        return [
          {
            label: t('page.guestJourney.filters.locations.all'),
            id: 'ALL',
          },
        ].concat(dataArray ?? []);
      },
      filter: isFilterVisible
        ? {
            type: 'MultiSelectFilter',
            label: t('page.guestJourney.filters.locations.label'),
          }
        : undefined,
    }),
  ];
};

/**
 * Toolbar-level date range — used by Movement, Offer Impact, Details tabs.
 * Active / Sliders / Churn pin server-side to last 90 days, so this filter is
 * not rendered on those tabs (the param is still declared so the value
 * persists across tab navigation).
 */
export const useRangeParams = (
  isFilterVisible: boolean,
  useSignUpLabel = false,
  rangeOptions?: DateRangeOption[],
) => {
  const { t } = useTranslation();
  return [
    luxonDateSearchParam('startdate', {
      defaultValue: RANGE_DEFAULT_START(),
      filter: isFilterVisible
        ? {
            type: 'DateRangeFilter',
            label: useSignUpLabel
              ? t('page.guestJourney.filters.range.signUpLabel')
              : t('page.guestJourney.filters.range.label'),
            showName: true,
            startParamName: 'startdate',
            endParamName: 'enddate',
            predefinedDateOptions:
              rangeOptions ??
              getDateRangeOptions([
                'lpLast7Days',
                'lpLast30Days',
                'lpLast60Days',
                'lpLast90Days',
                'lpLast365Days',
              ]),
            // Guest Journey date filters are preset-only (7/30/60/90/365, no
            // custom range) — the BE takes startdate/enddate either way, so the
            // presets just resolve to those two dates. (Member Count Trends
            // keeps its own custom picker via useTrendsRangeParams.)
            presetsOnly: true,
            clearable: false,
          }
        : undefined,
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
    luxonDateSearchParam('enddate', {
      defaultValue: RANGE_DEFAULT_END(),
      toQueryString: (value: DateTime) => value.toISODate(),
    }),
  ];
};

/**
 * Member Count Trends chart-scoped date range. Rendered inside the chart card
 * (not on the toolbar) on Active / Sliders / Churn. Declared on every tab so
 * the value survives tab navigation.
 */
export const useTrendsRangeParams = () => [
  luxonDateSearchParam('trendsStart', {
    defaultValue: TRENDS_RANGE_DEFAULT_START(),
    toQueryString: (value: DateTime) => value.toISODate(),
  }),
  luxonDateSearchParam('trendsEnd', {
    defaultValue: TRENDS_RANGE_DEFAULT_END(),
    toQueryString: (value: DateTime) => value.toISODate(),
  }),
];

/**
 * Stage Movement single-select. The stage list is the fixed 9-value taxonomy
 * (spend × visit combos); labels come from Guest Journey Settings so configured
 * tier names drive the filter.
 */
export const useStageParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  const [triggerSettings] = useLazyGetGuestJourneySettingsQuery();
  return [
    stringSearchParam('stage', {
      defaultValue: 'SPEND_TIER_2_VISIT_TIER_2',
      options: [],
      asyncOptions: async () => {
        const response = await triggerSettings();
        return toStageOptions(response?.data);
      },
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('page.guestJourney.filters.stage.label'),
          }
        : undefined,
    }),
  ];
};

/**
 * Offer Impact single-select. Options (the GJ-scoped offers with impact data)
 * and the default (the first such offer) are fetched up front by the Offer Impact
 * bootstrap and passed in — no `asyncOptions`, since the `offer` value maps to a
 * *required* `offerIds` query param and must be valid from first render (the
 * endpoints 400 without it). There is no "All Offers" option, per the design. On
 * other tabs the param is hidden; it is registered with empty options so its
 * value still persists across navigation.
 */
export const useOfferParams = (
  isFilterVisible: boolean,
  options: { id: string; label: string }[] = [],
  defaultValue = '',
) => {
  const { t } = useTranslation();
  return [
    stringSearchParam('offer', {
      defaultValue,
      options,
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('page.guestJourney.filters.offer.label'),
          }
        : undefined,
    }),
  ];
};

export const useCustomerTypeParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  return [
    stringSearchParam('customerType', {
      defaultValue: 'ACTIVE',
      options: [
        {
          label: t('page.guestJourney.filters.customerType.options.active'),
          id: 'ACTIVE',
        },
        {
          label: t('page.guestJourney.filters.customerType.options.sliding'),
          id: 'SLIDING',
        },
        {
          label: t('page.guestJourney.filters.customerType.options.churned'),
          id: 'CHURNED',
        },
      ],
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('page.guestJourney.filters.customerType.label'),
          }
        : undefined,
    }),
  ];
};

/**
 * Retention sign-up source single-select (Retention only). Static options.
 */
export const useSourceParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  return [
    stringSearchParam('source', {
      defaultValue: 'ALL',
      options: [
        { label: t('page.guestJourney.filters.source.options.all'), id: 'ALL' },
        {
          label: t('page.guestJourney.filters.source.options.imported'),
          id: 'IMPORTED',
        },
        { label: t('page.guestJourney.filters.source.options.ios'), id: 'IOS' },
        {
          label: t('page.guestJourney.filters.source.options.android'),
          id: 'ANDROID',
        },
        { label: t('page.guestJourney.filters.source.options.web'), id: 'WEB' },
        { label: t('page.guestJourney.filters.source.options.pos'), id: 'POS' },
      ],
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('page.guestJourney.filters.source.label'),
          }
        : undefined,
    }),
  ];
};

/**
 * Retention comparison-period single-select (Retention only). Static options.
 */
export const useCompareParams = (isFilterVisible: boolean) => {
  const { t } = useTranslation();
  return [
    stringSearchParam('compare', {
      defaultValue: 'PREV_30',
      options: [
        {
          label: t('page.guestJourney.filters.compare.options.prev30'),
          id: 'PREV_30',
        },
        {
          label: t('page.guestJourney.filters.compare.options.prev60'),
          id: 'PREV_60',
        },
        {
          label: t('page.guestJourney.filters.compare.options.prev90'),
          id: 'PREV_90',
        },
        {
          label: t('page.guestJourney.filters.compare.options.sameLastYear'),
          id: 'SAME_LAST_YEAR',
        },
      ],
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('page.guestJourney.filters.compare.label'),
          }
        : undefined,
    }),
  ];
};

/**
 * Benchmarks data-release year single-select (Benchmarks only). Options are
 * fetched up front by the Benchmarks bootstrap (`GET benchmarks/timeframes`) and
 * passed in as ready `{id,label}` pairs — no `asyncOptions`, since the year is a
 * required query param and must be a valid value from first render. The default
 * is the first (newest) option. On other tabs the param is hidden; it is
 * registered with empty options so its value still persists across navigation.
 */
export const useBenchmarkYearParams = (
  isFilterVisible: boolean,
  options: GuestJourneyBenchmarkTimeframe[] = [],
  defaultValue: string = options[0]?.id ?? '',
) => {
  const { t } = useTranslation();
  return [
    stringSearchParam('benchmarkYear', {
      defaultValue,
      options,
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('page.guestJourney.filters.benchmarkYear.label'),
          }
        : undefined,
    }),
  ];
};

/**
 * Benchmarks cuisine-category single-select (Benchmarks only). Options
 * (`GET clients/cuisine/list`) and the default (the client's own cuisine, from
 * `GET guest-journey/cuisine`) are fetched up front by the Benchmarks bootstrap and
 * passed in — no `asyncOptions`, since the cuisine is a required query param and
 * must be a valid value from first render. Each option carries a display-ready
 * `displayLabel` (the rendered label) separate from the `cuisine` value the
 * taxonomy is keyed by (the option id, sent as `benchmarkCuisine` and matched by
 * the default) — sc-371637. On other tabs the param is hidden; it is registered
 * with empty options so its value still persists across navigation.
 */
export const useBenchmarkCuisineParams = (
  isFilterVisible: boolean,
  options: GuestJourneyBenchmarkCuisine[] = [],
  defaultValue = '',
) => {
  const { t } = useTranslation();
  return [
    stringSearchParam('benchmarkCuisine', {
      defaultValue,
      options: options.map(({ cuisine, displayLabel }) => ({
        id: cuisine,
        label: displayLabel,
      })),
      filter: isFilterVisible
        ? {
            type: 'SingleSelectFilter',
            label: t('page.guestJourney.filters.benchmarkCuisine.label'),
          }
        : undefined,
    }),
  ];
};

/**
 * Every Guest Journey search param name. Tabs declare this full superset so
 * values persist across tab navigation, but each tab's queries must only send
 * the params that appear on its own filter bar — the email-pulse
 * `filteredSearchParams` pattern. Derive each tab's exclusion list from this
 * registry so the lists can't drift as params are added.
 */
const ALL_SEARCH_PARAM_NAMES = [
  'storeIds',
  'startdate',
  'enddate',
  'trendsStart',
  'trendsEnd',
  'stage',
  'offer',
  'customerType',
  'source',
  'compare',
  'benchmarkYear',
  'benchmarkCuisine',
];

const allParamsExcept = (visible: string[]) =>
  ALL_SEARCH_PARAM_NAMES.filter(name => !visible.includes(name));

/** Active / Sliders / Churn — only the Locations chip is on the toolbar. */
export const locationsOnlyFilteredSearchParams = allParamsExcept(['storeIds']);

/**
 * Member Count Trends (Active / Sliders / Churn) — the toolbar params plus
 * the chart-scoped trends date range.
 */
export const memberTrendsFilteredSearchParams = allParamsExcept([
  'storeIds',
  'trendsStart',
  'trendsEnd',
]);

/** Stage Movement — Locations + Date Range + Stage. */
export const stageMovementFilteredSearchParams = allParamsExcept([
  'storeIds',
  'startdate',
  'enddate',
  'stage',
]);

/** Offer Impact — Locations + Date Range + Offer. */
export const offerImpactFilteredSearchParams = allParamsExcept([
  'storeIds',
  'startdate',
  'enddate',
  'offer',
]);

/** Details — Locations + Date Range + Customer Type. */
export const detailsFilteredSearchParams = allParamsExcept([
  'storeIds',
  'startdate',
  'enddate',
  'customerType',
]);

/** Retention — Locations + Sign Up Date Range + Source + Compare. */
export const retentionFilteredSearchParams = allParamsExcept([
  'storeIds',
  'startdate',
  'enddate',
  'source',
  'compare',
]);

/**
 * Benchmarks — Year + Cuisine only. The benchmark comparison is defined by the
 * selected data-release year, so there is no toolbar date range; the endpoints
 * take just `year` + `benchmarkCuisine`.
 */
export const benchmarksFilteredSearchParams = allParamsExcept([
  'benchmarkYear',
  'benchmarkCuisine',
]);

const useAllSearchParams = (config: GuestJourneySearchParamsConfig) => [
  ...useLocationParams(config.storeIds),
  ...useRangeParams(config.range, config.rangeSignUpLabel, config.rangeOptions),
  ...useTrendsRangeParams(),
  ...useStageParams(config.stage),
  ...useOfferParams(config.offer, config.offerOptions, config.offerDefault),
  ...useCustomerTypeParams(config.customerType),
  ...useSourceParams(config.source ?? false),
  ...useCompareParams(config.compare ?? false),
  ...useBenchmarkYearParams(
    config.benchmarkYear ?? false,
    config.benchmarkYearOptions,
  ),
  ...useBenchmarkCuisineParams(
    config.benchmarkCuisine ?? false,
    config.benchmarkCuisineOptions,
    config.benchmarkCuisineDefault,
  ),
];

/**
 * Active Customers — only `Locations` chip is on the toolbar. The Member Count
 * Trends chart owns its own date picker (via `trendsStart` / `trendsEnd`).
 */
export const useActiveCustomersSearchParams = () =>
  useAllSearchParams({
    storeIds: true,
    range: false,
    trendsRange: false,
    stage: false,
    offer: false,
    customerType: false,
  });

/**
 * Sliders — same shape as Active Customers.
 */
export const useSlidersSearchParams = () =>
  useAllSearchParams({
    storeIds: true,
    range: false,
    trendsRange: false,
    stage: false,
    offer: false,
    customerType: false,
  });

/**
 * Churn — same shape as Active Customers.
 */
export const useChurnedSearchParams = () =>
  useAllSearchParams({
    storeIds: true,
    range: false,
    trendsRange: false,
    stage: false,
    offer: false,
    customerType: false,
  });

/**
 * Stage Movement — toolbar-level Locations + Range + Stage select.
 */
export const useStageMovementSearchParams = () =>
  useAllSearchParams({
    storeIds: true,
    range: true,
    trendsRange: false,
    stage: true,
    offer: false,
    customerType: false,
  });

/**
 * Offer Impact Movement — toolbar-level Locations + Range + Offer select. The
 * offer options + default come from the bootstrap (fetched up front) so the
 * required `offer` filter starts on a valid, real offer id.
 */
export const useOfferImpactSearchParams = (
  bootstrap: OfferImpactBootstrapData,
) =>
  useAllSearchParams({
    storeIds: true,
    range: true,
    trendsRange: false,
    stage: false,
    offer: true,
    customerType: false,
    offerOptions: bootstrap.offerOptions,
    offerDefault: bootstrap.offerDefault,
  });

/**
 * Details — toolbar-level Locations + Range + Customer Type.
 */
export const useDetailsSearchParams = () =>
  useAllSearchParams({
    storeIds: true,
    range: true,
    trendsRange: false,
    stage: false,
    offer: false,
    customerType: true,
  });

/**
 * Retention — toolbar-level Locations + Sign Up Date Range + Source + Compare.
 */
export const useRetentionSearchParams = () =>
  useAllSearchParams({
    storeIds: true,
    range: true,
    rangeSignUpLabel: true,
    trendsRange: false,
    stage: false,
    offer: false,
    customerType: false,
    source: true,
    compare: true,
  });

/**
 * Benchmarks — brand-level, so no Locations and no date range (the comparison is
 * scoped by the selected Year). Toolbar shows just the Benchmark Year and
 * Benchmark Cuisine selects, both populated from the bootstrap data so their
 * required query params have valid values from first render.
 */
export const useBenchmarksSearchParams = (bootstrap: BenchmarksBootstrapData) =>
  useAllSearchParams({
    storeIds: false,
    range: false,
    trendsRange: false,
    stage: false,
    offer: false,
    customerType: false,
    benchmarkYear: true,
    benchmarkCuisine: true,
    benchmarkYearOptions: bootstrap.yearOptions,
    benchmarkCuisineOptions: bootstrap.cuisineOptions,
    benchmarkCuisineDefault: bootstrap.cuisineDefault,
  });
